import { useState, useMemo, useRef, type FC } from 'react'
import { TextInput, Group, ActionIcon, Alert } from '@mantine/core'
import { IconCheck, IconX, IconAlertCircle } from '@tabler/icons-react'
import { genericMemo } from '../../../components/genericMemo'
import classes from './ThreadTitleInput.module.css'

export interface ThreadTitleInputProps {
    title?: string
    onSave: (newTitle: string) => void | Promise<unknown>
    placeholder?: string
}

export const ThreadTitleInput: FC<ThreadTitleInputProps> = genericMemo(
    ({ title, onSave, placeholder = 'Click to edit title...' }) => {
        const [currentValue, setCurrentValue] = useState(title || '')
        const [isActive, setIsActive] = useState(false) // Combines editing and focus states
        const [isPending, setIsPending] = useState(false)
        const [error, setError] = useState<Error | null>(null)
        const inputRef = useRef<HTMLInputElement>(null)

        const originalValue = useMemo(() => title || '', [title])

        const hasChanged = currentValue !== originalValue
        const canSave = hasChanged && currentValue.trim() !== '' && !isPending
        const showButtons = isActive || hasChanged

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            if (error) {
                setError(null)
            }
            setCurrentValue(event.target.value)
            setIsActive(true)
        }

        const handleSave = async () => {
            if (!canSave || isPending) return

            setIsPending(true)
            setError(null)

            try {
                const result = onSave(currentValue.trim())

                // Check if the result is a Promise
                if (result && typeof result === 'object' && 'then' in result) {
                    // It's a Promise, await it
                    await result
                } else {
                    // It's synchronous, add a small delay to show loading state
                    await new Promise((resolve) => setTimeout(resolve, 100))
                }

                // Success - reset state
                setIsActive(false)
                inputRef.current?.blur()
            } catch (err) {
                // Error - keep edit mode and show error
                setError(
                    err instanceof Error
                        ? err
                        : new Error('Failed to save title')
                )
            } finally {
                setIsPending(false)
            }
        }

        const handleCancel = () => {
            if (isPending) return
            setCurrentValue(originalValue)
            setIsActive(false)
            inputRef.current?.blur()
        }

        const handleKeyUp = (event: React.KeyboardEvent) => {
            if (isPending) return
            if (event.key === 'Enter' && canSave) {
                void handleSave()
            } else if (event.key === 'Escape') {
                handleCancel()
            }
        }

        const handleFocus = () => setIsActive(true)
        const handleBlur = () => setIsActive(false)

        return (
            <>
                <Group gap='sm' align='center' mb='md'>
                    <TextInput
                        ref={inputRef}
                        value={currentValue}
                        onChange={handleChange}
                        onKeyUp={handleKeyUp}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder={placeholder}
                        variant='unstyled'
                        size='xl'
                        disabled={isPending}
                        classNames={{
                            root: classes.root,
                            input: `${classes.input} mantine-focus-auto`,
                        }}
                    />

                    {showButtons && (
                        <>
                            <ActionIcon
                                variant='filled'
                                color='success'
                                size='sm'
                                onClick={() => void handleSave()}
                                disabled={!canSave}
                                loading={isPending}
                                aria-label='Save title'
                                title={
                                    isPending
                                        ? 'Saving...'
                                        : canSave
                                          ? 'Save changes'
                                          : 'Cannot save empty title'
                                }
                            >
                                <IconCheck size={16} />
                            </ActionIcon>
                            <ActionIcon
                                variant='filled'
                                color='error'
                                size='sm'
                                onClick={handleCancel}
                                disabled={isPending}
                                aria-label='Cancel changes'
                                title='Cancel changes'
                            >
                                <IconX size={16} />
                            </ActionIcon>
                        </>
                    )}
                </Group>

                {/* Error display */}
                {error && (
                    <Alert
                        icon={<IconAlertCircle size={16} />}
                        color='error'
                        variant='light'
                        mb='md'
                        title='Could not save the title'
                    >
                        Something went wrong saving your change. Please try
                        again.
                    </Alert>
                )}
            </>
        )
    }
)
